import { catalog, unwrap } from "../api";
import { BUSINESS_TIME_ZONE } from "../format";
import type { PagedResult } from "../../components/table/data-table";

/**
 * The customer home page's banners (`/api/catalog/homepage-banners`, `catalogue.administer`). Images
 * go up through the Files service (`useFileUpload` from `@steelyard/common`) and are referenced by hash.
 *
 * The three enums are hand-typed string unions: the catalog OpenAPI document types every enum as a
 * bare `number` while the service sends snake_case names (see `catalog.ts`'s `PriceRoundingMode`
 * note), so request and response bodies cast through `unknown`, the same idiom as the rest of the
 * catalogue calls.
 */
export type BannerPlacement =
  | "hero_left"
  | "hero_center"
  | "hero_right_top"
  | "hero_right_bottom"
  | "service_side"
  | "featured_side"
  | "category_panel"
  | "in_stock_right_top"
  | "in_stock_right_bottom"
  | "full_width"
  | "recommended_right_top"
  | "recommended_right_bottom"
  | "shop_the_range";

export type BannerLinkType = "category" | "brand" | "sku";

/**
 * Every slot on the home page, top to bottom, named by the section an admin sees it in.
 *
 * `recommended` is the image size that slot is drawn at on a sharp screen (the stock set seeded
 * with the module was cut to exactly these). `desktopWidth` is the slot's width in CSS pixels on a
 * 1440px-wide window, and `phone` the same on a 375px phone — `null` where the slot is hidden on
 * phones (`shownFrom` says from what width it appears). `shape: "image"` slots take the image's own
 * proportions (the strip, the grid tiles); the rest are cut to the slot's shape by the page layout.
 */
export type PlacementInfo = {
  value: BannerPlacement;
  label: string;
  size: string;
  recommended: { w: number; h: number };
  desktopWidth: number;
  /** On a phone: the slot's width, and its shape — the slot's, the image's own, or a fixed ratio. */
  phone: { width: number; shape: "slot" | "image" | number } | null;
  shownFrom?: string;
  shape: "slot" | "image";
  /** False where the page draws its own text over the image (the category panel's list). */
  showsText: boolean;
};

const XL = "screens 1200px wide and up";
const MD = "screens 768px wide and up";
export const PLACEMENTS: readonly PlacementInfo[] = [
  { value: "hero_left", label: "Hero — left", size: "tall, beside the centre banner", recommended: { w: 1000, h: 1794 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "hero_center", label: "Hero — centre", size: "the large opening banner", recommended: { w: 1600, h: 1382 }, desktopWidth: 640, phone: { width: 351, shape: "image" }, shape: "slot", showsText: true },
  { value: "hero_right_top", label: "Hero — right, top", size: "small, stacked", recommended: { w: 1000, h: 857 }, desktopWidth: 312, phone: null, shownFrom: MD, shape: "slot", showsText: true },
  { value: "hero_right_bottom", label: "Hero — right, bottom", size: "small, stacked", recommended: { w: 1000, h: 857 }, desktopWidth: 312, phone: null, shownFrom: MD, shape: "slot", showsText: true },
  { value: "service_side", label: "Service tiles — side", size: "beside the four service tiles", recommended: { w: 1000, h: 511 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "featured_side", label: "Featured — side", size: "tall, beside the Featured products", recommended: { w: 1000, h: 1743 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "category_panel", label: "In stock now — category panel", size: "tall; the popular-category list is drawn over it, so its text is not shown", recommended: { w: 1000, h: 1240 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: false },
  { value: "in_stock_right_top", label: "In stock now — right, top", size: "stacked pair", recommended: { w: 1000, h: 585 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "in_stock_right_bottom", label: "In stock now — right, bottom", size: "stacked pair", recommended: { w: 1000, h: 585 }, desktopWidth: 312, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "full_width", label: "Full-width strip", size: "a wide, short strip; cut to 3:1 on phones", recommended: { w: 3000, h: 309 }, desktopWidth: 1294, phone: { width: 351, shape: 3 }, shape: "image", showsText: true },
  { value: "recommended_right_top", label: "Recommended for you — right, top", size: "stacked pair", recommended: { w: 1200, h: 500 }, desktopWidth: 320, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "recommended_right_bottom", label: "Recommended for you — right, bottom", size: "stacked pair", recommended: { w: 1200, h: 548 }, desktopWidth: 320, phone: null, shownFrom: XL, shape: "slot", showsText: true },
  { value: "shop_the_range", label: "Shop the range — grid", size: "a grid tile; every live banner here is shown", recommended: { w: 1000, h: 635 }, desktopWidth: 305, phone: { width: 170, shape: "image" }, shape: "image", showsText: true },
];

export const placementInfo = (value: string) => PLACEMENTS.find((p) => p.value === value);

/** A mobile image's recommended size, for the placements a phone shows. */
export const MOBILE_RECOMMENDED: Partial<Record<BannerPlacement, { w: number; h: number }>> = {
  hero_center: { w: 1080, h: 1080 },
  full_width: { w: 1200, h: 400 },
  shop_the_range: { w: 800, h: 600 },
};

// --- text style ------------------------------------------------------------------------------

export type BannerTextSize = "small" | "medium" | "large" | "x_large";

/** `BannerStyle` on the wire (catalog's types say `number` for its enums; the service sends names). */
export type BannerStyle = {
  titleSize: BannerTextSize;
  subtitleSize: BannerTextSize;
  titleWeight: "medium" | "semibold" | "bold";
  align: "left" | "center" | "right";
  verticalAlign: "top" | "center" | "bottom";
  padding: "compact" | "normal" | "spacious";
  imageFocus: "left" | "center" | "right";
  overlay: "none" | "gradient" | "solid";
  /** Percent, 0–80 in steps of 10. */
  overlayOpacity: number;
  showButton: boolean;
  /** The text colour (`textTone`) was derived from the image on save. */
  autoTextColor: boolean;
  /** The overlay was derived from the image on save. */
  autoOverlay: boolean;
};

const BASE_STYLE: BannerStyle = {
  titleSize: "medium",
  subtitleSize: "medium",
  titleWeight: "semibold",
  align: "left",
  verticalAlign: "top",
  padding: "normal",
  imageFocus: "right",
  overlay: "none",
  overlayOpacity: 0,
  showButton: true,
  autoTextColor: false,
  autoOverlay: false,
};
const SIDE: Partial<BannerStyle> = { titleSize: "large", subtitleSize: "small", titleWeight: "bold" };
const COMPACT: Partial<BannerStyle> = { titleSize: "medium", subtitleSize: "small", padding: "compact" };

/**
 * How the storefront draws a banner with no saved style — a copy of the portal's own
 * (`steelyard-portal/lib/banner-style.ts`), kept in step by hand. Editing such a banner starts here,
 * so it looks the same until someone changes it.
 */
export const BANNER_PRESETS: Record<BannerPlacement, BannerStyle> = {
  hero_center: { ...BASE_STYLE, titleSize: "x_large", titleWeight: "bold", padding: "spacious" },
  hero_left: { ...BASE_STYLE, ...SIDE },
  service_side: { ...BASE_STYLE, ...SIDE },
  featured_side: { ...BASE_STYLE, ...SIDE },
  category_panel: BASE_STYLE,
  hero_right_top: { ...BASE_STYLE, ...COMPACT },
  hero_right_bottom: { ...BASE_STYLE, ...COMPACT },
  in_stock_right_top: { ...BASE_STYLE, ...COMPACT },
  in_stock_right_bottom: { ...BASE_STYLE, ...COMPACT },
  recommended_right_top: { ...BASE_STYLE, ...COMPACT },
  recommended_right_bottom: { ...BASE_STYLE, ...COMPACT },
  shop_the_range: { ...BASE_STYLE, ...COMPACT },
  full_width: { ...BASE_STYLE, subtitleSize: "small", titleWeight: "bold", verticalAlign: "center" },
};

/** A new banner's style: its placement's preset, centred on the image, colour and overlay read from the image. */
export const newBannerStyle = (placement: BannerPlacement): BannerStyle => ({
  ...BANNER_PRESETS[placement],
  imageFocus: "center",
  autoTextColor: true,
  autoOverlay: true,
});

export const placementLabel = (value: BannerPlacement) => PLACEMENTS.find((p) => p.value === value)?.label ?? value;

export type HomepageBanner = {
  id: string;
  /** The banner's URL key (`bannerPath`): assigned from its name at create, never changed, not editable. */
  slug: string;
  name: string;
  placement: BannerPlacement;
  desktopImageHash: string;
  mobileImageHash: string | null;
  title: string | null;
  subtitle: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  linkType: BannerLinkType | null;
  linkTargetId: string | null;
  altText: string | null;
  textTone: "dark" | "light";
  displayOrder: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  /** On the home page right now — the API's own answer, so the browser's clock never decides it. */
  isLive: boolean;
  /** Null until someone styles the banner; the storefront then draws its placement's preset. */
  style: BannerStyle | null;
};

export type HomepageBannerInput = Omit<HomepageBanner, "id" | "slug" | "isLive">;

export async function listBanners(placement?: BannerPlacement) {
  return unwrap(
    (await catalog()).GET("/api/catalog/homepage-banners", { params: { query: placement ? { placement } : {} } }),
  ) as unknown as Promise<PagedResult<HomepageBanner>>;
}

export async function getBanner(id: string) {
  return unwrap((await catalog()).GET("/api/catalog/homepage-banners/{id}", { params: { path: { id } } })) as unknown as Promise<HomepageBanner>;
}

export async function getBannerBySlug(slug: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/homepage-banners/by-slug/{slug}", { params: { path: { slug } } }),
  ) as unknown as Promise<HomepageBanner>;
}

export async function createBanner(input: HomepageBannerInput) {
  const data = await unwrap((await catalog()).POST("/api/catalog/homepage-banners", { body: input as never }));
  return data as unknown as { id: string };
}

export async function updateBanner(id: string, input: HomepageBannerInput) {
  await unwrap((await catalog()).PUT("/api/catalog/homepage-banners/{id}", { params: { path: { id } }, body: input as never }));
}

/** A real delete — nothing else refers to a banner. Its images stay in the image store. */
export async function deleteBanner(id: string) {
  await unwrap((await catalog()).DELETE("/api/catalog/homepage-banners/{id}", { params: { path: { id } } }));
}

/** The given banners become display order 1, 2, 3… in this order. */
export async function reorderBanners(ids: readonly string[]) {
  await unwrap((await catalog()).PUT("/api/catalog/homepage-banners/order", { body: { ids: [...ids] } }));
}

/** A row's own fields, for a write that changes one of them (the status switch). */
export function inputOf({ id: _id, slug: _slug, isLive: _isLive, ...input }: HomepageBanner): HomepageBannerInput {
  return input;
}

/**
 * One placement's banners with `id` moved one place up (-1) or down (+1), as the id order
 * `reorderBanners` takes — or null when it is already at that end.
 */
export function movedOrder(rows: readonly HomepageBanner[], id: string, step: -1 | 1): string[] | null {
  const row = rows.find((r) => r.id === id);
  if (!row) return null;
  const siblings = rows.filter((r) => r.placement === row.placement).map((r) => r.id);
  const from = siblings.indexOf(id);
  const to = from + step;
  if (to < 0 || to >= siblings.length) return null;
  [siblings[from], siblings[to]] = [siblings[to]!, siblings[from]!];
  return siblings;
}

// --- schedule: whole days, in the business's own zone -----------------------------------------

const WALL_CLOCK = (timeZone: string) =>
  new Intl.DateTimeFormat("sv-SE", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });

/** How far `timeZone`'s wall clock is ahead of UTC at `instant`, in ms. */
const offsetAt = (instant: number, timeZone: string) =>
  Date.parse(`${WALL_CLOCK(timeZone).format(instant).replace(" ", "T")}Z`) - instant;

/**
 * "2026-10-20" → that day's midnight in `timeZone`, whatever zone the admin's browser is in. The
 * offset is read twice so a daylight-saving change between UTC midnight and local midnight lands right.
 */
export function midnightIn(isoDate: string, timeZone: string): number {
  const utcMidnight = Date.parse(`${isoDate}T00:00:00Z`);
  const guess = utcMidnight - offsetAt(utcMidnight, timeZone);
  return utcMidnight - offsetAt(guess, timeZone);
}

const dateIn = (instant: number) => new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TIME_ZONE }).format(new Date(instant));

/**
 * The form's start and end days → the API's instants. The end day is inclusive — the banner is
 * shown through the whole of it — so the API's end-exclusive `endsAt` is the next day's midnight.
 */
export function scheduleToApi(start: string, end: string): { startsAt: string | null; endsAt: string | null } {
  const nextDay = (isoDate: string) => new Date(Date.parse(`${isoDate}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
  return {
    startsAt: start ? new Date(midnightIn(start, BUSINESS_TIME_ZONE)).toISOString() : null,
    endsAt: end ? new Date(midnightIn(nextDay(end), BUSINESS_TIME_ZONE)).toISOString() : null,
  };
}

/** The reverse of `scheduleToApi`, for the edit form and the list. */
export function scheduleFromApi(banner: Pick<HomepageBanner, "startsAt" | "endsAt">): { start: string; end: string } {
  return {
    start: banner.startsAt ? dateIn(Date.parse(banner.startsAt)) : "",
    end: banner.endsAt ? dateIn(Date.parse(banner.endsAt) - 1) : "",
  };
}

// --- image files ---------------------------------------------------------------------------------

export const BANNER_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;

/** Catalog's own image ceiling (`Catalog:Images:MaxUploadBytes`, 5 MB): refused here before uploading, not after. */
export const BANNER_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Why this file cannot be a banner image, or null. Checked before the upload, not after. */
export function bannerImageProblem(file: File): string | null {
  if (!(BANNER_IMAGE_TYPES as readonly string[]).includes(file.type)) return `${file.name} is not a JPEG, PNG, WebP or GIF image.`;
  if (file.size > BANNER_IMAGE_MAX_BYTES) {
    const megabytes = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(file.size / 1024 / 1024);
    return `${file.name} is ${megabytes} MB; the limit is 5 MB. Export it smaller — a web banner rarely needs more than 1 MB.`;
  }
  return null;
}

/**
 * A soft warning about an image's pixel size against what its slot is drawn at — smaller looks soft,
 * a very different shape is cropped at the edges. Never blocks a save.
 */
export function bannerImageFit(
  natural: { w: number; h: number },
  recommended: { w: number; h: number },
  followsImage: boolean,
): string | null {
  if (natural.w < recommended.w * 0.6 || natural.h < recommended.h * 0.6) {
    return `This image is ${natural.w} × ${natural.h}px; the slot is drawn at up to ${recommended.w} × ${recommended.h}px, so it may look soft.`;
  }
  const ratio = natural.w / natural.h / (recommended.w / recommended.h);
  if (!followsImage && (ratio > 1.35 || ratio < 0.74)) {
    return "This image is a different shape from the slot, so its edges are cropped — check the preview, and use Image focus to keep the part that matters.";
  }
  return null;
}
